/**
 * Provider-agnostic analytics shim.
 * The Google tag (GA4 measurement id) loads on every page with Consent Mode v2
 * default denied, so Tag Assistant can see it before a choice. Analytics events
 * still wait for analytics consent. Optional Meta / TikTok snippets load only
 * with marketing consent + IDs.
 *
 * Google Ads conversions are imported from GA4 (`store_click`). There is no AW-
 * snippet. `GOOGLE_ADS_AW_ID` is reserved and unused while it stays empty.
 */

import { env } from "@/lib/env";
import {
  COOKIE_CONSENT_EVENT,
  hasAnalyticsConsent,
  hasMarketingConsent,
  readCookieConsent,
} from "@/lib/cookie-consent";
import { storePlatformFromHref } from "@/lib/store-urls";

type EventName =
  | "page_view"
  | "form_submit_started"
  | "form_submit_succeeded"
  | "form_submit_failed"
  | "cta_clicked"
  | "founder_strip_view"
  | "store_click";

/** Optional Google Ads tag id (`AW-…`). Empty: do not load a second conversion tag. */
export const GOOGLE_ADS_AW_ID = "";

export const CONSENT_DEFAULT = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
  wait_for_update: 500,
  url_passthrough: true,
  ads_data_redaction: true,
} as const;

export type ConsentGrant = {
  analytics: boolean;
  marketing: boolean;
};

export type StoreClickName = "google_play" | "app_store" | "qr";

export function consentStateFromGrants(grants: ConsentGrant) {
  const analytics = grants.analytics ? "granted" : "denied";
  const ads = grants.marketing ? "granted" : "denied";
  return {
    ad_storage: ads,
    ad_user_data: ads,
    ad_personalization: ads,
    analytics_storage: analytics,
  } as const;
}

type Payload = Record<string, string | number | boolean | undefined>;

type InitFlags = {
  analytics: boolean;
  marketing: boolean;
};

/** Campaign context injected by LandingConfigProvider (one-shot, no polling). */
let campaignContext: {
  landing_variant?: string;
  utm_campaign?: string;
} = {};

export function setAnalyticsCampaignContext(next: {
  landing_variant?: string;
  utm_campaign?: string;
}): void {
  campaignContext = {
    landing_variant: next.landing_variant,
    utm_campaign: next.utm_campaign,
  };
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
    ttq?: { load: (id: string) => void; page: () => void; track?: (...args: unknown[]) => void };
    plausible?: (event: string, opts: { props: Payload }) => void;
    posthog?: { capture: (event: string, payload: Payload) => void };
  }
}

let googleTagBootstrapped = false;
let pageViewSentFor: string | null = null;
let metaReady = false;
let tiktokReady = false;
/** One store_click per native click, even if a button and a parent both handle it. */
let seenStoreClickEvents = new WeakSet<Event>();

export function resetGoogleTagForTests(): void {
  googleTagBootstrapped = false;
  pageViewSentFor = null;
  metaReady = false;
  tiktokReady = false;
  seenStoreClickEvents = new WeakSet();
}

function debugModeEnabled(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("debug_mode") === "1";
}

function canTrackAnalytics(): boolean {
  if (typeof window === "undefined") return false;
  if (env.analytics.provider === "none") return true;
  return hasAnalyticsConsent();
}

export function onCookieConsentChange(
  handler: (grantedAnalytics: boolean) => void,
): () => void {
  if (typeof window === "undefined") return () => undefined;
  const stored = readCookieConsent();
  handler(stored?.analytics === true);
  const listener = (event: Event) => {
    const detail = (event as CustomEvent<{ analytics?: boolean }>).detail;
    handler(detail?.analytics === true);
  };
  window.addEventListener(COOKIE_CONSENT_EVENT, listener);
  return () => window.removeEventListener(COOKIE_CONSENT_EVENT, listener);
}

/**
 * Queue Consent Mode default, then load gtag.js for the GA4 id.
 * A stored or fresh choice is applied with `consent` `update` in the same turn,
 * before the library script is inserted. Later calls only update consent.
 * `GOOGLE_ADS_AW_ID` is not configured.
 */
export function bootstrapGoogleTag(
  measurementId: string,
  grants?: ConsentGrant,
  reapply = false,
): void {
  if (typeof window === "undefined" || !measurementId) return;
  if (googleTagBootstrapped) {
    if (reapply && grants) applyGoogleConsent(grants);
    return;
  }

  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    // gtag.js only executes a command when it is an Arguments object
    // (`dataLayer.push(arguments)`). A rest-parameter array is ignored,
    // so consent updates and events never reach collect.
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer?.push(arguments);
    };
  }
  window.gtag("consent", "default", { ...CONSENT_DEFAULT });
  if (grants) {
    window.gtag("consent", "update", consentStateFromGrants(grants));
    if (!grants.analytics) pageViewSentFor = null;
  }

  window.gtag("js", new Date());
  const config: Record<string, unknown> = { send_page_view: false };
  if (debugModeEnabled()) config.debug_mode = true;
  window.gtag("config", measurementId, config);

  if (!document.getElementById("ga4-gtag")) {
    const script = document.createElement("script");
    script.id = "ga4-gtag";
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    document.head.appendChild(script);
  }
  googleTagBootstrapped = true;
}

export function applyGoogleConsent(grants: ConsentGrant): void {
  if (typeof window === "undefined" || !googleTagBootstrapped) return;
  if (!grants.analytics) pageViewSentFor = null;
  window.gtag?.("consent", "update", consentStateFromGrants(grants));
}

export function initAnalyticsProviders(flags: InitFlags): void {
  if (typeof window === "undefined") return;

  if (env.analytics.provider === "ga4" && env.analytics.ga4MeasurementId) {
    bootstrapGoogleTag(
      env.analytics.ga4MeasurementId,
      {
        analytics: flags.analytics,
        marketing: flags.marketing,
      },
      true,
    );
  }

  if (flags.marketing) {
    if (env.analytics.metaPixelId) {
      ensureMetaBase(env.analytics.metaPixelId);
    }
    if (env.analytics.tiktokPixelId) {
      ensureTiktokBase(env.analytics.tiktokPixelId);
    }
  } else {
    revokeMarketingPixels();
  }
}

/** Best-effort: treat ad pixels as not consented after prefs update. */
function revokeMarketingPixels() {
  metaReady = false;
  tiktokReady = false;
  if (typeof window === "undefined") return;
  window.fbq = (..._args: unknown[]) => {
    /* marketing consent withdrawn — no-op */
  };
  window.ttq = {
    load: () => undefined,
    page: () => undefined,
    track: () => undefined,
  };
}

/** Meta Pixel base snippet only — no custom conversion events (Phase 3). */
function ensureMetaBase(pixelId: string) {
  if (!hasMarketingConsent() || metaReady) return;

  type FbqFn = ((...args: unknown[]) => void) & {
    callMethod?: (...args: unknown[]) => void;
    queue?: unknown[];
    push?: (...args: unknown[]) => void;
    loaded?: boolean;
    version?: string;
  };

  const stub: FbqFn = (...args: unknown[]) => {
    if (!hasMarketingConsent()) return;
    if (stub.callMethod) {
      stub.callMethod(...args);
    } else {
      stub.queue = stub.queue || [];
      stub.queue.push(args);
    }
  };
  stub.queue = [];
  stub.push = stub;
  stub.loaded = true;
  stub.version = "2.0";
  window.fbq = stub;

  if (!document.getElementById("meta-pixel")) {
    const script = document.createElement("script");
    script.id = "meta-pixel";
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(script);
  }

  window.fbq?.("init", pixelId);
  window.fbq?.("track", "PageView");
  metaReady = true;
}

/** TikTok Pixel base snippet only — no conversion events (Phase 3). */
function ensureTiktokBase(pixelId: string) {
  if (!hasMarketingConsent() || tiktokReady) return;

  // Minimal stub until the vendor script hydrates.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ttq: any = {
    _q: [] as unknown[],
    load(id: string) {
      if (!hasMarketingConsent()) return;
      ttq._i = ttq._i || {};
      ttq._i[id] = [];
    },
    page() {
      if (!hasMarketingConsent()) return;
      ttq._q.push(["page"]);
    },
    track(...args: unknown[]) {
      if (!hasMarketingConsent()) return;
      ttq._q.push(["track", ...args]);
    },
  };
  window.ttq = ttq;

  if (!document.getElementById("tiktok-pixel")) {
    const script = document.createElement("script");
    script.id = "tiktok-pixel";
    script.async = true;
    script.src = "https://analytics.tiktok.com/i18n/pixel/events.js";
    document.head.appendChild(script);
  }

  window.ttq?.load(pixelId);
  window.ttq?.page();
  tiktokReady = true;
}

export function trackPageView(path?: string): void {
  if (!canTrackAnalytics()) return;
  const resolved =
    path ?? (typeof window !== "undefined" ? window.location.pathname : "");
  if (!resolved || pageViewSentFor === resolved) return;
  pageViewSentFor = resolved;
  track("page_view", { path: resolved });
}

export function trackStoreClick(input: {
  store: StoreClickName;
  placement: string;
  /** Native DOM event for this tap. The same event is recorded at most once. */
  event?: Event;
}): void {
  if (typeof window === "undefined" || !canTrackAnalytics()) return;
  if (input.event) {
    if (seenStoreClickEvents.has(input.event)) return;
    seenStoreClickEvents.add(input.event);
  }
  const pageLocation = window.location.href;
  const payload = {
    store: input.store,
    placement: input.placement,
    page_location: pageLocation,
  };
  if (env.analytics.provider === "ga4" && env.analytics.ga4MeasurementId) {
    bootstrapGoogleTag(env.analytics.ga4MeasurementId, {
      analytics: hasAnalyticsConsent(),
      marketing: hasMarketingConsent(),
    });
    window.gtag?.("event", "store_click", {
      ...payload,
      transport_type: "beacon",
      ...(debugModeEnabled() ? { debug_mode: true } : {}),
    });
    return;
  }
  track("store_click", payload);
}

/** Fire `store_click` when `href` is an App Store or Play Store URL. Leaves `href` unchanged. */
export function trackStoreClickFromHref(
  href: string,
  placement: string,
  event?: Event,
): void {
  const platform = storePlatformFromHref(href);
  if (!platform) return;
  trackStoreClick({
    store: platform === "android" ? "google_play" : "app_store",
    placement,
    event,
  });
}

export function track(event: EventName, payload: Payload = {}): void {
  // Re-check storage on every call so prefs updates stop tracking immediately.
  if (!canTrackAnalytics()) return;

  const enriched: Payload = {
    ...payload,
    ...(campaignContext.landing_variant
      ? { landing_variant: campaignContext.landing_variant }
      : {}),
    ...(campaignContext.utm_campaign
      ? { utm_campaign: campaignContext.utm_campaign }
      : {}),
  };

  switch (env.analytics.provider) {
    case "ga4":
      dispatchGa4(event, enriched);
      break;
    case "plausible":
      dispatchPlausible(event, enriched);
      break;
    case "posthog":
      dispatchPosthog(event, enriched);
      break;
    default:
      if (!env.isProd) {
        console.debug("[analytics]", event, enriched);
      }
  }
}

function dispatchGa4(event: EventName, payload: Payload) {
  if (typeof window === "undefined" || !env.analytics.ga4MeasurementId) return;
  bootstrapGoogleTag(env.analytics.ga4MeasurementId);
  if (event === "page_view") {
    window.gtag?.("event", "page_view", {
      page_path: payload.path,
      send_to: env.analytics.ga4MeasurementId,
      ...(debugModeEnabled() ? { debug_mode: true } : {}),
    });
    return;
  }
  window.gtag?.("event", event, payload);
}

function dispatchPlausible(event: string, payload: Payload) {
  if (typeof window === "undefined") return;
  window.plausible?.(event, { props: payload });
}

function dispatchPosthog(event: string, payload: Payload) {
  if (typeof window === "undefined") return;
  window.posthog?.capture(event, payload);
}
